import { useEffect, useState } from "react";
import { updateLoginOtpChannel } from "../../services/SignUpReport/SignUpReport";
import { getAdminProfile } from "../../services/authService";
import {
  ArrowRightLeft,
  Loader2,
  Mail,
  ShieldCheck,
  Smartphone,
} from "lucide-react";

export default function LoginSecurity() {
  const [otpChannel, setOtpChannel] = useState("sms");
  const [isToggling, setIsToggling] = useState(false);

  const handleToggleChannel = async () => {
    if (isToggling) return;

    setIsToggling(true);
    const targetChannel = otpChannel === "sms" ? "email" : "sms";

    try {
      const res = await updateLoginOtpChannel({
        channel: targetChannel,
      });

      if (res?.status?.code === 200) {
        setOtpChannel(res?.data?.channel);
      }
    } catch (error) {
      console.log(error);
    }

    setIsToggling(false);
  };

  const targetChannelText = otpChannel === "sms" ? "email" : "sms";

  useEffect(() => {
    const getProfile = async () => {
      try {
        const response = await getAdminProfile();
        setOtpChannel(response?.otp_channel);
      } catch (error) {
        console.error("Error fetching profile details", error);
      }
    };

    getProfile();
  }, []);

  return (
    <div className="bg-slate-50 font-sans min-h-screen flex flex-col items-center p-4 sm:p-8">
      <div className="w-full max-w-4xl space-y-6">
        {/* Header Container */}
        <div className="flex justify-between items-center gap-6 px-6 py-4 bg-white/70 backdrop-blur-xl border border-cyan-100 shadow-[0_4px_20px_-4px_rgba(6,182,212,0.1)] rounded-2xl">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl border border-cyan-100/70 shadow-inner">
              <ShieldCheck className="w-6 h-6 text-blue-600" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-blue-700 to-cyan-500 tracking-tight">
              Login security
            </h1>
          </div>
        </div>

        {/* Settings Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          {/* Card Header */}
          <div className="px-6 py-5 border-b border-slate-100 bg-slate-50/50">
            <h2 className="text-lg font-bold text-slate-800">OTP Settings</h2>
            <p className="text-sm text-slate-500 mt-1">
              Configure how users receive their verification codes during login.
            </p>
          </div>

          {/* Card Body */}
          <div className="p-6 sm:p-10 flex flex-col sm:flex-row items-center justify-between gap-10">
            
            {/* Current State Display */}
            <div className="flex flex-col items-center justify-center space-y-3 p-8 w-full sm:w-1/2 bg-gradient-to-b from-slate-50 to-white rounded-2xl border border-slate-100">
              <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">
                Current Channel
              </p>
              <div className="flex items-center gap-3 text-3xl font-bold text-slate-800">
                {otpChannel === "sms" ? (
                  <>
                    <Smartphone className="w-8 h-8 text-blue-500" /> SMS
                  </>
                ) : (
                  <>
                    <Mail className="w-8 h-8 text-cyan-500" /> Email
                  </>
                )}
              </div>
            </div>

            {/* Action Area */}
            <div className="flex flex-col items-center justify-center space-y-6 w-full sm:w-1/2">
              <div className="text-center space-y-2">
                <p className="text-base text-slate-600">
                  Change Login OTP channel to{" "}
                  <strong className="text-cyan-600 font-semibold uppercase">
                    {targetChannelText}
                  </strong>
                </p>
                <p className="text-sm text-slate-400 max-w-xs mx-auto">
                  Users will receive their verification codes via {targetChannelText}.
                </p>
              </div>

              {/* Custom Toggle Switch */}
              <button
                onClick={handleToggleChannel}
                disabled={isToggling}
                className={`
                  relative inline-flex h-10 w-20 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                  transition-colors duration-300 ease-in-out focus:outline-none focus-visible:ring-2 
                  focus-visible:ring-cyan-500 focus-visible:ring-offset-2 shadow-inner
                  ${otpChannel === "email" ? "bg-cyan-500" : "bg-slate-300"}
                  ${isToggling ? "opacity-80 cursor-not-allowed" : "hover:opacity-90"}
                `}
                role="switch"
                aria-checked={otpChannel === "email"}
              >
                <span className="sr-only">Toggle OTP Channel</span>

                {/* Background Icons in the track */}
                <div className="absolute inset-0 flex items-center justify-between px-2 text-white pointer-events-none">
                  <Smartphone className="w-4 h-4 opacity-70" />
                  <Mail className="w-4 h-4 opacity-70" />
                </div>

                {/* Thumb / Knob */}
                <span
                  className={`
                    pointer-events-none flex items-center justify-center h-9 w-9 transform rounded-full 
                    bg-white shadow-md ring-0 transition duration-300 ease-in-out absolute top-0
                    ${otpChannel === "email" ? "translate-x-10" : "translate-x-0"}
                  `}
                >
                  {isToggling ? (
                    <Loader2 className="w-5 h-5 text-cyan-500 animate-spin" />
                  ) : (
                    <ArrowRightLeft className="w-4 h-4 text-slate-400" />
                  )}
                </span>
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}